import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ConsumeSyncRun {
  id: number;
  table_name: string;
  mode: string | null;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  rows_upserted: number | null;
  source_count: number | null;
  mirror_count: number | null;
  error: string | null;
}

export type ConsumeSyncState =
  | { state: 'awaiting-apply' }
  | { state: 'error'; error: Error }
  | { state: 'loading' }
  | { state: 'success'; latestByTable: Map<string, ConsumeSyncRun> };

// ─────────────────────────────────────────────────────────────────────────────
// Error classification
// ─────────────────────────────────────────────────────────────────────────────

const TABLE_MISSING_CODES = new Set(['42P01', 'PGRST205']);

const isTableMissingError = (error: { code?: string; message?: string }): boolean => {
  if (error.code && TABLE_MISSING_CODES.has(error.code)) return true;
  const msg = error.message?.toLowerCase() ?? '';
  if (msg.includes('relation') && msg.includes('does not exist')) return true;
  if (msg.includes('could not find the table')) return true;
  return false;
};

// ─────────────────────────────────────────────────────────────────────────────
// Raw query hook (internal)
// ─────────────────────────────────────────────────────────────────────────────

const useConsumeSyncRunsRaw = () => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ConsumeSyncRun[], Error, ConsumeSyncRun[], readonly unknown[]>({
    queryKey: ['consume-sync-runs', userId, organizationId],
    queryFn: async (): Promise<ConsumeSyncRun[]> => {
      if (organizationId === null) return [];
      const { data, error } = await supabase
        .from('mktg_consume_sync_runs')
        .select(
          'id, table_name, mode, status, started_at, finished_at, rows_upserted, source_count, mirror_count, error'
        )
        .eq('organization_id', organizationId)
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data ?? []) as ConsumeSyncRun[];
    },
    retry: (failureCount, error) => {
      if (isTableMissingError(error as { code?: string; message?: string })) return false;
      // One retry for transient network errors; monitoring page will stale-refetch anyway.
      return failureCount < 1;
    },
    staleTime: 30000,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Public hook — derived state with graceful awaiting-apply handling
// ─────────────────────────────────────────────────────────────────────────────

export const useConsumeSyncStatus = (): ConsumeSyncState => {
  const query = useConsumeSyncRunsRaw();

  if (query.isLoading) {
    return { state: 'loading' };
  }

  if (query.error) {
    if (isTableMissingError(query.error as { code?: string; message?: string })) {
      return { state: 'awaiting-apply' };
    }
    return { state: 'error', error: query.error };
  }

  const rows = query.data ?? [];

  // Reduce to latest run per table_name (rows are pre-sorted desc by started_at)
  const latestByTable = new Map<string, ConsumeSyncRun>();
  for (const row of rows) {
    if (!latestByTable.has(row.table_name)) {
      latestByTable.set(row.table_name, row);
    }
  }

  return { state: 'success', latestByTable };
};
